import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { Modal, Tabs, Tab, Table } from "react-bootstrap";
import { DeleteCustomerBankDetails } from "../services/CustomerToFranchaiase.service";
import notistack from "modules/core/notistack";

interface BankFormModalProps {
    isOpen: boolean;
    onclose: () => void;
    bankDetails(data: any): Promise<any>;
    customerBankDetails: any | null;
    selectedBankDetails?: any | null;
    setSelectedBankDetails?(data: any | null): void;
    setBankId(id: number): void;
    isSubmitting: boolean;
    getBankDetails?: () => Promise<void>;
}

const BankFormModal = ({
    bankDetails,
    isOpen,
    onclose,
    isSubmitting,
    customerBankDetails,
    selectedBankDetails,
    setSelectedBankDetails,
    setBankId,
    getBankDetails
}: BankFormModalProps) => {

    const [activeTab, setActiveTab] = useState("form");
    const [localBankDetails, setLocalBankDetails] = useState<any[]>([]);
    const [newlyAddedBankId, setNewlyAddedBankId] = useState<number | null>(null);

    const [formData, setFormData] = useState({
        bankName: "",
        accountNo: "",
        ifsc: "",
        accountHolderName: "",
        url: "",
        fileBase64: ""
    });

    const [errors, setErrors] = useState<any>({});
    const [isFileSelected, setIsFileSelected] = useState(false);

    useEffect(() => {
        if (customerBankDetails && Array.isArray(customerBankDetails)) {
            setLocalBankDetails(customerBankDetails);
        }
    }, [customerBankDetails]);


    useEffect(() => {
        if (newlyAddedBankId && customerBankDetails && Array.isArray(customerBankDetails)) {
            const foundBank = customerBankDetails.find((bank: any) => bank.id === newlyAddedBankId);
            if (foundBank) {
                setSelectedBankDetails?.(foundBank);
                setNewlyAddedBankId(null);
            }
        }
    }, [customerBankDetails]);

    const fileInputRef = useRef<HTMLInputElement>(null);

    // Update resetForm function to clear file input
    const resetForm = () => {
        setFormData({
            bankName: "",
            accountNo: "",
            ifsc: "",
            accountHolderName: "",
            url: "",
            fileBase64: ""
        });
        setErrors({});
        setIsFileSelected(false);

        // Clear the file input value
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };


    const handleChange = (e: any) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrors((prev: any) => ({
            ...prev,
            [name]: "",
        }));
    };

    const convertToBase64 = (file: File) => {
        return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);
        });
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];

        if (file) {
            const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif", "application/pdf"];
            if (!allowedTypes.includes(file.type)) {
                notistack.warning("Only Image and PDF files are allowed");
                e.target.value = "";
                return;
            }
            const maxSize = 5 * 1024 * 1024;
            if (file.size > maxSize) {
                notistack.warning("File size should not exceed 5MB");
                e.target.value = "";
                return;
            }
            const base64 = await convertToBase64(file);
            setFormData((prev) => ({
                ...prev,
                url: file.name,
                fileBase64: base64
            }));
            setIsFileSelected(true);
            setErrors((prev: any) => ({
                ...prev,
                url: "",
                fileBase64: ""
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                url: "",
                fileBase64: ""
            }));
            setIsFileSelected(false);


            if (!isSubmitting) {
                setErrors((prev: any) => ({
                    ...prev,
                    url: "Cancel cheque upload is required"
                }));
            }
        }
    };

    const handleFileClick = () => {
        setTimeout(() => {
            if (fileInputRef.current && !fileInputRef.current.files?.length && !isFileSelected) {
                setFormData((prev) => ({
                    ...prev,
                    url: "",
                    fileBase64: ""
                }));
                setIsFileSelected(false);
            }
        }, 300);
    };

    const validate = () => {
        const newErrors: any = {};
        const bankName = formData.bankName?.trim() || "";
        const accountHolderName = formData.accountHolderName?.trim() || "";
        const accountNo = formData.accountNo?.replace(/\D/g, "") || "";
        const ifsc = formData.ifsc?.replace(/\s/g, "").toUpperCase() || "";

        if (!bankName) { newErrors.bankName = "Bank name is required"; }
        else if (bankName.length < 3) { newErrors.bankName = "Bank name must be at least 3 characters"; }
        else if (bankName.length > 50) { newErrors.bankName = "Bank name must be less than 50 characters"; }
        else if (!/^[a-zA-Z\s.&()-]+$/.test(bankName)) { newErrors.bankName = "Bank name contains invalid characters"; }

        if (!accountHolderName) { newErrors.accountHolderName = "Account holder name is required"; }
        else if (accountHolderName.length < 3) { newErrors.accountHolderName = "Must be at least 3 characters"; }
        else if (accountHolderName.length > 100) { newErrors.accountHolderName = "Must be less than 100 characters"; }
        else if (!/^[a-zA-Z\s.'-]+$/.test(accountHolderName)) { newErrors.accountHolderName = "Invalid characters in name"; }

        if (!accountNo) { newErrors.accountNo = "Account number is required"; }
        else if (!/^\d{9,18}$/.test(accountNo)) { newErrors.accountNo = "Account number must be 9–18 digits"; }

        if (!ifsc) { newErrors.ifsc = "IFSC is required"; }
        else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) { newErrors.ifsc = "Invalid IFSC format (e.g. HDFC0001234)"; }

        // Check if file is selected
        if (!formData.fileBase64 && !isFileSelected) {
            newErrors.url = "Cancel cheque upload is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        if (!validate()) return;

        const formattedData = {
            bankName: formData.bankName.trim(),
            accountHolderName: formData.accountHolderName.trim(),
            accountNo: formData.accountNo.replace(/\D/g, ""),
            ifsc: formData.ifsc.replace(/\s/g, "").toUpperCase(),
            url: formData.fileBase64,
        };

        const res = await bankDetails(formattedData);

        if (res) {
            resetForm();
            setBankId(res);
            setNewlyAddedBankId(res);

            if (getBankDetails) {
                await getBankDetails();
            }

            setActiveTab("list");
        }
    };

    const handleSelect = (item: any) => {
        setSelectedBankDetails?.(item);
        setBankId(item.id);
    };

    const handleClearSelection = () => {
        setSelectedBankDetails?.(null);
        setBankId(0);
    };

    const handleDelete = async (item: any) => {
        if (!item) return;
        try {
            const response = await DeleteCustomerBankDetails(item.id);
            if (response.status === 200) {
                if (selectedBankDetails?.id === item.id) {
                    handleClearSelection();
                }

                if (getBankDetails) {
                    await getBankDetails();
                }

                notistack.notifyFromApi(response);
            } else {
                notistack.notifyFromApi(response);
            }
        } catch (error: any) {
            notistack.error(error);
        }
    };

    const handleModalClose = () => {
        resetForm();
        onclose();
    };

    return (
        <Modal show={isOpen} onHide={handleModalClose} size="lg" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Bank Details</Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ maxHeight: '350px', overflow: 'auto' }}>

                <Tabs activeKey={activeTab} onSelect={(k: any) => setActiveTab(k)}>
                    <Tab eventKey="form" title="Add Bank">

                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="row">

                                    <div className="col-md-6 mb-3">
                                        <label className="mb-1">
                                            Bank Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            name="bankName"
                                            value={formData.bankName}
                                            onChange={handleChange}
                                            className={`form-control form-control-sm ${errors.bankName ? "is-invalid" : ""
                                                }`}
                                            placeholder="Enter bank name"
                                        />
                                        {errors.bankName && (
                                            <small className="text-danger">{errors.bankName}</small>
                                        )}
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <label className="mb-1">
                                            Account Holder <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            name="accountHolderName"
                                            value={formData.accountHolderName}
                                            onChange={handleChange}
                                            className={`form-control form-control-sm ${errors.accountHolderName ? "is-invalid" : ""
                                                }`}
                                            placeholder="Enter name"
                                        />
                                        {errors.accountHolderName && (
                                            <small className="text-danger">
                                                {errors.accountHolderName}
                                            </small>
                                        )}
                                    </div>
                                </div>
                                <div className="row">

                                    <div className="col-md-6 mb-3">
                                        <label className="mb-1">
                                            Account No <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            name="accountNo"
                                            value={formData.accountNo}
                                            onChange={(e) => {
                                                e.target.value = e.target.value.replace(/\D/g, "");
                                                handleChange(e);
                                            }}
                                            maxLength={18}
                                            className={`form-control form-control-sm ${errors.accountNo ? "is-invalid" : ""
                                                }`}
                                            placeholder="Enter account number"
                                        />
                                        {errors.accountNo && (
                                            <small className="text-danger">{errors.accountNo}</small>
                                        )}
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <label className="mb-1">
                                            IFSC <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            name="ifsc"
                                            value={formData.ifsc}
                                            onChange={(e) => {
                                                e.target.value = e.target.value.toUpperCase().replace(/\s/g, "");
                                                handleChange(e);
                                            }}
                                            maxLength={11}
                                            className={`form-control form-control-sm ${errors.ifsc ? "is-invalid" : ""
                                                }`}
                                            placeholder="Enter IFSC"
                                        />
                                        {errors.ifsc && (
                                            <small className="text-danger">{errors.ifsc}</small>
                                        )}
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <label className="mb-1">
                                            Upload Cancel Cheque <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            onChange={handleFileChange}
                                            onClick={handleFileClick}
                                            className={`form-control form-control-sm ${errors.url ? "is-invalid" : ""}`}
                                            accept="image/*,application/pdf"
                                        />
                                        {errors.url && (
                                            <small className="text-danger">{errors.url}</small>
                                        )}
                                        {/* {formData.url && !errors.url && (
                                            <small className="text-success mt-1 d-block">
                                                Selected file: {formData.url}
                                            </small>
                                        )} */}
                                    </div>
                                </div>
                                <div className="d-flex justify-content-end mt-3">

                                    <button type="submit" className="btn submit-btn btn-sm" disabled={isSubmitting}>
                                        {isSubmitting ? "Submitting..." : "Submit"}
                                    </button>
                                </div>

                            </div>
                        </form>

                    </Tab>

                    <Tab eventKey="list" title="Existing Banks">

                        <Table bordered hover size="sm" className="mt-3">
                            <thead>
                                <tr>
                                    <th>Action</th>
                                    <th>Bank Name</th>
                                    <th>Account Holder</th>
                                    <th>Account No</th>
                                </tr>
                            </thead>

                            <tbody>
                                {localBankDetails && localBankDetails.length > 0 ? (
                                    localBankDetails.map((details: any) => (
                                        <tr key={details.id}>
                                            <td className="d-flex align-items-center gap-3">
                                                <button
                                                    className="btn btn-sm action-btn delete-btn"
                                                    title="Delete"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleDelete(details)}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>

                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    style={{ cursor: 'pointer' }}
                                                    checked={selectedBankDetails?.id === details?.id}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            handleSelect(details);
                                                        } else {
                                                            handleClearSelection();
                                                        }
                                                    }}
                                                />
                                            </td>

                                            <td>{details?.bankName}</td>
                                            <td>{details?.accountHolderName}</td>
                                            <td>{details?.accountNo}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={4} className="text-center text-muted">
                                            No Bank Details Found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>

                    </Tab>

                </Tabs>

            </Modal.Body>
        </Modal>
    );
};

export default BankFormModal;